// Column definitions for the Governance DataTables.
// Mirrors the columns-file pattern used by
// Domain/domainCoverageColumns and Concept/utilities/columns.
import { CheckCircle2 } from "lucide-react";
import type { Column } from "../../../../components/DataTable";
import { toTitleCaseFromSnake } from "../../utils";
import type { GControl, GFinding } from "../governanceTypes";
import { severityTone, strengthTone } from "./helpers";
import { SEVERITY_PILL_BASE_CLS } from "./constants";

export const controlColumns: Column<GControl>[] = [
  {
    key: "control_id",
    header: "ID",
    align: "left",
    width: "80px",
    render: (row) => (
      <span className="text-[11.5px] font-semibold text-foreground">
        {row.control_id}
      </span>
    ),
  },
  {
    key: "control_name",
    header: "Control",
    align: "left",
    minWidth: 220,
    render: (row) => (
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[12px] font-semibold text-foreground break-words">
          {row.control_name}
        </span>
        <span className="text-[10.5px] text-muted-foreground">
          {toTitleCaseFromSnake(row.category)}
        </span>
      </div>
    ),
  },
  {
    key: "present",
    header: "Present",
    align: "center",
    width: "90px",
    render: (row) =>
      row.present ? (
        <CheckCircle2 className="mx-auto size-4 text-emerald-400" />
      ) : (
        <span className="text-[11.5px] text-red-400">Missing</span>
      ),
  },
  {
    key: "strength",
    header: "Strength",
    align: "center",
    width: "110px",
    render: (row) => {
      const t = strengthTone(row.strength);
      return (
        <span className={`${SEVERITY_PILL_BASE_CLS} ${t.bg} ${t.text}`}>
          {t.label}
        </span>
      );
    },
  },
  {
    key: "source",
    header: "Source",
    align: "left",
    width: "140px",
    render: (row) => (
      <span className="text-[11px] text-muted-foreground break-words">
        {row.source_location}
      </span>
    ),
  },
];

export const findingColumns: Column<GFinding>[] = [
  {
    key: "finding_type",
    header: "Finding",
    align: "left",
    minWidth: 160,
    render: (row) => (
      <span className="text-[12px] font-semibold text-foreground break-words">
        {toTitleCaseFromSnake(row.finding_type)}
      </span>
    ),
  },
  {
    key: "affected_control",
    header: "Affected control",
    align: "left",
    minWidth: 180,
    render: (row) => (
      <span className="text-[12px] text-foreground break-words">
        {row.affected_control}
      </span>
    ),
  },
  {
    key: "description",
    header: "Description",
    align: "left",
    ellipsis: 2,
    tooltip: (row) => row.description,
    render: (row) => (
      <span className="text-[12px] text-muted-foreground break-words">
        {row.description}
      </span>
    ),
  },
  {
    key: "severity",
    header: "Severity",
    align: "center",
    width: "100px",
    render: (row) => {
      const t = severityTone(row.severity);
      return (
        <span className={`${SEVERITY_PILL_BASE_CLS} ${t.bg} ${t.text}`}>
          {t.label}
        </span>
      );
    },
  },
  {
    key: "source",
    header: "Source",
    align: "left",
    width: "140px",
    render: (row) => (
      <span className="text-[11px] text-muted-foreground break-words">
        {(row.source_location ?? []).join(", ")}
      </span>
    ),
  },
];
